import { useState } from "react";
import { Rocket, CheckCircle2, XCircle, Circle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// True per-provider status, read from the real connection state (not hard-coded).
export function StatusBadge({ p }) {
  const connected = !!p.connected;
  const failed = !connected && p?.health?.state === "error";
  if (connected)
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`row-status-${p.provider_id}`}><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Connected</span>;
  if (failed)
    return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700" data-testid={`row-status-${p.provider_id}`}><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Connection failed</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500" data-testid={`row-status-${p.provider_id}`}><span className="h-1.5 w-1.5 rounded-full bg-slate-300" /> Not Connected</span>;
}

// Computes whether a connected provider's results actually reach customers right now.
export function isLive(p, settings) {
  return !!(p.connected && p.enabled && settings && !settings.no_api_mode &&
    settings.comparison?.enabled && (p.surfaces || {}).comparison);
}

// One-click "Go live" control + a plain-language blockers checklist. `apiBase` is the panel's
// admin route prefix (e.g. "/admin/service-integrations"). `onChanged` reloads the list.
export function GoLiveControl({ p, settings, apiBase, onChanged }) {
  const [busy, setBusy] = useState(false);
  const live = isLive(p, settings);
  const connected = !!p.connected;

  const toggle = async () => {
    setBusy(true);
    try {
      await api.post(`${apiBase}/providers/${p.provider_id}/go-live`, { live: !live });
      toast.success(!live ? `${p.name} is live for customers` : `${p.name} taken off customer surfaces`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!connected) {
    return <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-400" data-testid={`golive-blocked-${p.provider_id}`}>Connect first to go live</span>;
  }

  const blockers = [];
  if (settings?.no_api_mode) blockers.push("No-API Mode on");
  if (!settings?.comparison?.enabled) blockers.push("Comparison surface off");
  if (!(p.surfaces || {}).comparison) blockers.push("This provider's surface off");

  return (
    <div className="flex flex-col items-end gap-1">
      <button onClick={toggle} disabled={busy} data-testid={`golive-toggle-${p.provider_id}`}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50 ${live ? "bg-emerald-600 hover:bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700"}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : live ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Rocket className="h-3.5 w-3.5" />}
        {live ? "Live · turn off" : "Go live"}
      </button>
      {live ? (
        <span className="text-[10px] font-medium text-emerald-600" data-testid={`golive-status-${p.provider_id}`}>Showing to customers</span>
      ) : blockers.length ? (
        <span className="text-right text-[10px] text-slate-400" data-testid={`golive-blockers-${p.provider_id}`}>Go live will turn on: {blockers.join(" · ")}</span>
      ) : (
        <span className="text-[10px] text-slate-400" data-testid={`golive-blockers-${p.provider_id}`}>Ready — one tap to show customers</span>
      )}
    </div>
  );
}
